import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useSelector, useDispatch } from "react-redux";
import { AuthContext } from "../../../../Header/auth - context";
import { Calendar } from "primereact/calendar";
import CustomDropdown from "../../../../custom/customDropdown";

/* Datum se salje kroz toUtcIso — bez korekcije offseta toISOString ume da
   pomeri datum za dan unazad. */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + 'Z';
};

const TransferZaposlenih = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    /* Zaposleni izabran u Pregledu (ili na drugom tabu) — nosi se kroz sve
       tabove, pa se ovde nudi kao već izabran. */
    const employee = useSelector((state) => state.employee);
    const dispatch = useDispatch();
    const toast = useRef(null);

    const [zaposleniDB, setZaposleniDB] = useState([]);
    const [firmeDB, setFirmeDB] = useState([]);
    const [idZaposleni, setIdZaposleni] = useState(null);
    const [novaFirma, setNovaFirma] = useState(null);
    const [pocetakURadu, setPocetakURadu] = useState(null);
    const [salje, setSalje] = useState(false);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const ucitaj = () => {
        fetch(process.env.REACT_APP_BACKEND_URL + "/employees/transfer", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ step: "INIT" }),
        })
            .then((r) => r.json())
            .then((data) => {
                setZaposleniDB(data.ZaposleniDB || []);
                setFirmeDB(data.FirmeDB || []);
            })
            .catch(() => poruka("error", t("common.greska"), t("transfer.greskaUcitavanje")));
    };

    useEffect(() => {
        ucitaj();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Zajednički izbor upada u polje čim spisak stigne — samo ako ovde još
       nema izbora i ako je taj zaposleni među ponuđenima za transfer. */
    useEffect(() => {
        if (!employee || zaposleniDB.length === 0) return;
        const opcija = zaposleniDB.find((z) => String(z.value) === String(employee));
        if (!opcija) return;
        setIdZaposleni((prev) => (prev == null ? opcija.value : prev));
    }, [employee, zaposleniDB]);

    const izabrani = zaposleniDB.find((z) => z.value === idZaposleni);
    const ciljna = firmeDB.find((f) => f.value === novaFirma);

    const potvrdi = () => {
        if (!idZaposleni || !novaFirma) {
            poruka("warn", t("common.upozorenje"), t("transfer.izaberiOboje"));
            return;
        }
        if (!pocetakURadu) {
            poruka("warn", t("common.upozorenje"), t("transfer.izaberiDatum"));
            return;
        }
        if (izabrani && izabrani.firma_id === novaFirma) {
            poruka("warn", t("common.upozorenje"), t("transfer.istaFirma"));
            return;
        }

        confirmDialog({
            message: t("transfer.potvrdaPitanje", {
                zaposleni: izabrani?.label || "",
                stara: izabrani?.TrenutnaFirma || "",
                nova: ciljna?.label || "",
            }),
            header: t("transfer.potvrdaNaslov"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"),
            rejectLabel: t("common.ne"),
            acceptClassName: "p-button-danger",
            accept: izvrsi,
        });
    };

    const izvrsi = () => {
        setSalje(true);
        fetch(process.env.REACT_APP_BACKEND_URL + "/employees/transfer", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ step: "TRANSFER", id_zaposleni: idZaposleni,
                nova_firma: novaFirma, employmentStart: toUtcIso(pocetakURadu) }),
        })
            .then(async (r) => {
                const data = await r.json();
                if (!r.ok) throw new Error(data.message || t("transfer.greskaTransfer"));
                return data;
            })
            .then((data) => {
                poruka("success", t("transfer.naslov"), data.message || t("transfer.uspesno"));
                /* Posle transfera se pušta i zajednički izbor — zaposleni je
                   otišao u drugu firmu, nema smisla da ostane izabran. */
                dispatch({ type: "EMPLOYEE", value: null });
                setIdZaposleni(null);
                setNovaFirma(null);
                setPocetakURadu(null);
                /* Lista se osvežava jer zaposleni posle transfera može da nestane
                   iz vidokruga trenutne firme. */
                ucitaj();
            })
            .catch((e) => poruka("error", t("common.greska"), e.message))
            .finally(() => setSalje(false));
    };

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <div className="transfer-forma">
                        <div className="transfer-polje">
                            <label htmlFor="zaposleni">{t("transfer.zaposleni")}:</label>
                            <CustomDropdown
                                id="zaposleni"
                                name="zaposleni"
                                style={{ width: "100%" }}
                                options={zaposleniDB}
                                value={idZaposleni}
                                optionLabel="label"
                                placeholder={t("transfer.phZaposleni")}
                                onChange={(e) => {
                                    /* Izbor ovde menja i zajednički izbor, da ga
                                       ostali tabovi zateknu istog. */
                                    dispatch({ type: "EMPLOYEE", value: e.target.value ?? null });
                                    setIdZaposleni(e.target.value);
                                }}
                            />
                            {izabrani && (
                                <small className="transfer-trenutna">
                                    {t("transfer.trenutnaFirma")}: <b>{izabrani.TrenutnaFirma}</b>
                                </small>
                            )}
                        </div>

                        <div className="transfer-polje">
                            <label htmlFor="novaFirma">{t("transfer.novaFirma")}:</label>
                            <CustomDropdown
                                id="novaFirma"
                                name="novaFirma"
                                style={{ width: "100%" }}
                                options={firmeDB}
                                value={novaFirma}
                                optionLabel="label"
                                placeholder={t("transfer.phFirma")}
                                onChange={(e) => setNovaFirma(e.target.value)}
                            />
                        </div>

                        <div className="transfer-polje">
                            <label htmlFor="pocetakURadu">{t("transfer.pocetakURadu")}:</label>
                            <Calendar id="pocetakURadu" value={pocetakURadu}
                                onChange={(e) => setPocetakURadu(e.value)}
                                dateFormat="dd.mm.yy" showIcon style={{ width: "100%" }}
                                placeholder={t("transfer.phDatum")} />
                        </div>
                    </div>

                    <div className="transfer-disclaimer">
                        <i className="pi pi-info-circle" />
                        <span>{t("transfer.disclaimer")}</span>
                    </div>

                    <div className="formUnosZap buttonUnosZap">
                        <button
                            type="button"
                            onClick={potvrdi}
                            disabled={salje}
                            className="btn btn-success"
                            style={{ minWidth: "20%", marginTop: "2%", textTransform: "uppercase" }}
                        >
                            {t("admin.unos")}
                        </button>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default TransferZaposlenih;
